import { AnchorProvider, BN, Program, web3, } from "@project-serum/anchor/dist/cjs";
import { SolanaAugmentedProvider, } from "@saberhq/solana-contrib";
import { getATAAddress, getOrCreateATA } from "@saberhq/token-utils";
import * as spl from "@solana/spl-token";
import { PublicKey, SystemProgram } from "@solana/web3.js";
import randomBytes from "randombytes";
import { IDL } from "../target/types/syrup";
import { SYRUP_ADDRESSES } from "./constants";
import { findLenderAddress } from "./pda";
function toBN(num) {
    if (typeof num === "number") {
        return new BN(num);
    }
    return num;
}
async function getAndEnsureATA(provider, mint, owner) {
    const address = await getATAAddress({ mint, owner });
    if (!(await provider.getAccountInfo(address))) {
        throw new Error("Syrup SDK: ATA account does not exist");
    }
    return address;
}
export class Nonce {
    constructor(value) {
        this.value = value;
    }
    static generate() {
        return new Nonce(randomBytes(8));
    }
    asParam() {
        return { value: [...this.value] };
    }
}
/** Main class for the Syrup SDK */
export class SyrupClient {
    constructor(provider, program) {
        this.provider = provider;
        this.program = program;
    }
    // Entrypoint for the sdk given a provider
    static load({ provider, addresses = SYRUP_ADDRESSES, }) {
        const allAddresses = { ...SYRUP_ADDRESSES, ...addresses };
        const anchorProvider = new AnchorProvider(provider.connection, provider.wallet, provider.opts);
        const program = new Program(IDL, allAddresses.Syrup.toString(), anchorProvider);
        return new SyrupClient(new SolanaAugmentedProvider(provider), program);
    }
    /**
     * Finds a program address given seeds.
     * @param seeds The seeds of the program address to compute.
     * @returns The derived address.
     */
    async findProgramAddress(seeds) {
        return await PublicKey.findProgramAddress(seeds.map((seed) => {
            if (typeof seed === "string") {
                return Buffer.from(seed);
            }
            if (seed instanceof PublicKey) {
                return seed.toBytes();
            }
            if (seed instanceof Nonce) {
                return seed.value;
            }
            return seed;
        }), this.program.programId);
    }
    /**
     * Constructs a new SyrupClient with a particular signer.
     * @param signer The signer to use when creating instructions.
     * @returns A SyrupClient with the given signer.
     */
    withSigner(signer) {
        return new SyrupClient(this.provider.withSigner(signer), this.program);
    }
    /**
     * Gets the class with all the global admin actions.
     * @returns Global admin actions.
     */
    globalAdminActions() {
        return new GlobalAdminActions(this);
    }
    /**
     * Gets the class with all the governor actions.
     * @returns Governor actions.
     */
    governorActions() {
        return new GovernorActions(this);
    }
    /**
     * Gets the class with the pool delegate actions.
     * @returns Pool delegate actions
     */
    poolDelegateActions() {
        return new PoolDelegateActions(this);
    }
    /**
     * Gets the class with the lender actions.
     * @returns Lender actions.
     */
    lenderActions() {
        return new LenderActions(this);
    }
    /**
     * Gets the class with the borrower actions.
     * @returns Borrower actions.
     */
    borrowerActions() {
        return new BorrowerActions(this);
    }
}
/** All actions taken by the global admin. */
export class GlobalAdminActions {
    constructor(client) {
        this.client = client;
    }
    /**
     * Gets the public key of the provider.
     */
    get publicKey() {
        return this.client.provider.wallet.publicKey;
    }
    /**
     * Sets the state of the protocol to be paused, this halting
     * certain instructions from being executed.
     * @param pause Whether to pause or unpause the protocol.
     * @param globals The globals wrapper to use.
     * @param globalAdmin Public key of the global admin.
     * @returns Transaction Envelope.
     */
    async setProtocolPause({ pause, globals, globalAdmin = this.client.provider.wallet.publicKey, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsSetProtocolPause(pause, {
                accounts: {
                    globals: globals.address,
                    globalAdmin,
                },
            }),
        ]);
    }
}
/** Actions taken by the governor of the protocol. */
export class GovernorActions {
    constructor(client) {
        this.client = client;
    }
    /**
     * Gets the public key of the provider.
     */
    get publicKey() {
        return this.client.provider.wallet.publicKey;
    }
    /**
     * Initializes a globals object to act as the central state
     * storage for protocol level data.
     * @param governor The governor's public key. Defaults to signer.
     * @param globalAdmin The global admin's public key.
     * @param treasuryDrawdownFee The fee in 1e18 units to be paid to the treasury upon drawdown.
     * @returns Transaction Envelope.
     */
    async initializeGlobals({ governor = this.client.provider.wallet.publicKey, globalAdmin, treasuryDrawdownFee, }) {
        const nonce = Nonce.generate();
        const [globals] = await this.client.findProgramAddress(["globals", nonce]);
        return {
            key: globals,
            tx: this.client.provider.newTX([
                this.client.program.instruction.globalsInitialize(nonce.asParam(), toBN(treasuryDrawdownFee), {
                    accounts: {
                        governor,
                        globalAdmin,
                        globals,
                        systemProgram: SystemProgram.programId,
                        rent: web3.SYSVAR_RENT_PUBKEY,
                    },
                }),
            ]),
        };
    }
    /**
     * Adds a new delegate to the globals object.
     * @param globals The globals wrapper to use.
     * @param delegate The new delegate to add.
     * @returns Transaction Envelope.
     */
    async setDelegate({ globals, delegate, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsSetDelegate(delegate, {
                accounts: {
                    governor: globals.data.governor,
                    globals: globals.address,
                },
            }),
        ]);
    }
    /**
     * Removes a delegate from the globals object.
     * @param globals The globals wrapper to use.
     * @param delegate The delegate to remove.
     * @returns Transaction Envelope.
     */
    async removeDelegate({ globals, delegate, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsRemoveDelegate(delegate, {
                accounts: {
                    governor: globals.data.governor,
                    globals: globals.address,
                },
            }),
        ]);
    }
    /**
     * Adds a new token mint to the globals object.
     * @param globals The globals wrapper to use.
     * @param baseMint The address of the new mint to add.
     * @returns Transaction Envelope.
     */
    async setBaseMint({ globals, baseMint, }) {
        // We use getOrCreateATA instead of the initializeTreasury
        // command since it's possible the treasury has been
        // initialized by one setBaseMint call, removed, then
        // re-added. They perform the same purpose since
        // initializeTreasury just creates an ATA.
        const { instruction: ix } = await getOrCreateATA({
            provider: this.client.provider,
            mint: baseMint,
            owner: globals.address,
        });
        return this.client.provider.newTX([
            ix,
            this.client.program.instruction.globalsSetBaseMint(baseMint, {
                accounts: {
                    governor: globals.data.governor,
                    globals: globals.address,
                },
            }),
        ]);
    }
    /**
     * Removes a token mint from the globals object.
     * @param globals The globals wrapper to use.
     * @param baseMint The address of the mint to remove.
     * @returns Transaction Envelope.
     */
    async removeBaseMint({ globals, baseMint, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsRemoveBaseMint(baseMint, {
                accounts: {
                    governor: globals.data.governor,
                    globals: globals.address,
                },
            }),
        ]);
    }
    /**
     * Sets a pending governor that needs to be accepted.
     * by the receiving governor.
     * @param globals The globals wrapper to change.
     * @param governor The governor of the protocol. Optional.
     * @param nextGovernor The next governor of the protocol.
     * @returns Transaction Envelope.
     */
    async setPendingGovernor({ globals, governor = this.client.provider.wallet.publicKey, nextGovernor, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsSetPendingGovernor(nextGovernor, {
                accounts: {
                    globals: globals.address,
                    governor,
                },
            }),
        ]);
    }
    /**
     * The pending governor calls this function to accept the transfer.
     * @param globals The globals wrapper to change.
     * @param governorNominee The pending governor.
     * @returns Transaction Envelope.
     */
    async acceptGovernor({ globals, governorNominee = this.client.provider.wallet.publicKey, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsAcceptGovernor({
                accounts: {
                    globals: globals.address,
                    governorNominee,
                },
            }),
        ]);
    }
    /**
     * Sets a new global admin directly.
     * @param globals The globals wrapper to change.
     * @param governor The governor of the protocol. Optional.
     * @param newAdmin The new global admin.
     * @returns Transaction Envelope.
     */
    async setGlobalAdmin({ globals, governor = this.client.provider.wallet.publicKey, newAdmin, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsSetGlobalAdmin({
                accounts: {
                    globals: globals.address,
                    governor,
                    newAdmin,
                },
            }),
        ]);
    }
    /**
     * Sets the globals treasury drawdown fee. Not allowed to be
     * greater than 50%.
     * @param globals The globals wrapper to change.
     * @param governor The governor of the protocol. Optional.
     * @param treasuryDrawdownFee The new treasury drawdown fee in 1e18.
     * @returns Transaction Envelope.
     */
    async setGlobalTreasuryDrawdownFee({ globals, governor = this.client.provider.wallet.publicKey, treasuryDrawdownFee, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.globalsSetTreasuryDrawdownFee(toBN(treasuryDrawdownFee), {
                accounts: {
                    globals: globals.address,
                    governor,
                },
            }),
        ]);
    }
    /**
     * Initializes a new globals treasury for a particular mint.
     * This is used for storing the funds from drawdown and repayment.
     * @param globals The globals wrapper to change.
     * @param governor The governor of the protocol. Optional.
     * @param baseMint The base mint to use.
     * @returns Transaction Envelope.
     */
    async initializeTreasury({ globals, governor = this.client.provider.wallet.publicKey, baseMint, }) {
        const { address: treasuryLocker } = await getOrCreateATA({
            provider: this.client.provider,
            mint: baseMint,
            owner: globals.address,
        });
        return this.client.provider.newTX([
            this.client.program.instruction.globalsInitTreasury({
                accounts: {
                    globals: globals.address,
                    governor,
                    treasuryLocker,
                    baseMint,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                    associatedTokenProgram: spl.ASSOCIATED_TOKEN_PROGRAM_ID,
                    rent: web3.SYSVAR_RENT_PUBKEY,
                },
            }),
        ]);
    }
    /**
     * Claims the treasury fees from a particular mint, ie USDC.
     * @param globals The globals wrapper to claim fees from.
     * @param governor The governor of the protocol. Optional.
     * @param governorLocker The destination for governor funds.
     * @param claimAmount The amount of fees to claim.
     * @param baseMint Mint to claim from.
     * @returns Transaction Envelope.
     */
    async claimTreasuryFees({ globals, governor = this.client.provider.wallet.publicKey, governorLocker, claimAmount, baseMint, }) {
        const governorLockerAccount = governorLocker
            ? {
                address: governorLocker,
                instruction: null,
            }
            : await getOrCreateATA({
                provider: this.client.provider,
                mint: baseMint,
            });
        const { address: treasuryLocker, instruction: createTreasuryIx } = await getOrCreateATA({
            provider: this.client.provider,
            mint: baseMint,
            owner: globals.address,
        });
        return this.client.provider.newTX([
            governorLockerAccount.instruction,
            createTreasuryIx,
            this.client.program.instruction.globalsClaimTreasuryFees(toBN(claimAmount), {
                accounts: {
                    globals: globals.address,
                    governor,
                    governorLocker: governorLockerAccount.address,
                    treasuryLocker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
}
/** Actions taken by a Pool Delegate. */
export class PoolDelegateActions {
    constructor(client) {
        this.client = client;
    }
    /**
     * Gets the public key of the provider.
     */
    get publicKey() {
        return this.client.provider.wallet.publicKey;
    }
    /**
     * Initializes a new pool attached to a particular globals object.
     * @param globals The globals wrapper to attach to.
     * @param delegate The pool delegate. Optional.
     * @param baseMint The token mint of what loans in the pool should be denominated in.
     * @param openToPublic Whether or not lenders need to be whitelisted before depositing.
     * @param lockupPeriod Seconds before a withdrawal request can be opened.
     * @param cooldownPeriod Seconds before a withdrawal request can be executed.
     * @param withdrawValidityWindow Seconds after a withdrawal request is opened that it expires.
     * @param liquidityCap The maximum amount of tokens that can be deposited into the pool.
     * @param delegateDrawdownFee Fee for delegate on drawdown. Denominated in 1e18.
     * @param delegateRepaymentFee Fee for delegate on each repayment. Denominated in 1e18.
     * @param treasuryRepaymentFee Fee for treasury on each repayment. Denominated in 1e18.
     * @returns Transaction Envelope.
     */
    async initializePool({ globals, delegate = this.client.provider.wallet.publicKey, baseMint, openToPublic, lockupPeriod, cooldownPeriod, withdrawValidityWindow, liquidityCap, delegateDrawdownFee, delegateRepaymentFee, treasuryRepaymentFee, }) {
        const nonce = Nonce.generate();
        const [pool] = await this.client.findProgramAddress([
            "pool",
            globals.address,
            nonce,
        ]);
        const [delegateClaimable] = await this.client.findProgramAddress([
            "delegate_claimable",
            pool,
        ]);
        const [poolLocker] = await this.client.findProgramAddress([
            "pool_locker",
            pool,
        ]);
        const [sharesMint] = await this.client.findProgramAddress([
            "pool_shares",
            pool,
        ]);
        const poolConfig = {
            openToPublic,
            lockupPeriod: toBN(lockupPeriod),
            cooldownPeriod: toBN(cooldownPeriod),
            withdrawValidityWindow: toBN(withdrawValidityWindow),
            liquidityCap: toBN(liquidityCap),
            treasuryRepaymentFee: toBN(treasuryRepaymentFee),
            delegateDrawdownFee: toBN(delegateDrawdownFee),
            delegateRepaymentFee: toBN(delegateRepaymentFee),
        };
        return {
            pool,
            poolLocker,
            sharesMint,
            tx: this.client.provider.newTX([
                this.client.program.instruction.poolInitialize(nonce.asParam(), poolConfig, {
                    accounts: {
                        globals: globals.address,
                        delegate,
                        delegateClaimable,
                        pool,
                        baseMint,
                        poolLocker,
                        sharesMint,
                        systemProgram: SystemProgram.programId,
                        tokenProgram: spl.TOKEN_PROGRAM_ID,
                        rent: web3.SYSVAR_RENT_PUBKEY,
                    },
                }),
            ]),
        };
    }
    /**
     * Marks a given lender as allowlisted for pools not open to public.
     * @param pool The pool that this lender is associated with.
     * @param lenderUser The owner of the Lender account to whitelist.
     * @param allowlisted Boolean to set their allowlisted status to.
     * @param delegate The pool delegate. Optional.
     * @returns Transaction Envelope.
     */
    async setAllowlisted({ pool, lenderUser, allowlisted, delegate = this.client.provider.wallet.publicKey, }) {
        const [lender] = await this.client.findProgramAddress([
            "lender",
            pool.address,
            lenderUser,
        ]);
        return this.client.provider.newTX([
            this.client.program.instruction.lenderSetAllowlisted(allowlisted, {
                accounts: {
                    globals: pool.data.globals,
                    pool: pool.address,
                    lender,
                    delegate,
                },
            }),
        ]);
    }
    /**
     * Sets configuration parameters for a pool.
     * @param pool Pool to set params for.
     * @param delegate Pool Delegate. Optional.
     * @param config New pool config.
     * @returns Transaction Envelope.
     */
    async setConfig({ pool, delegate = this.client.provider.wallet.publicKey, config, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.poolSetConfig(config, {
                accounts: {
                    globals: pool.data.globals,
                    pool: pool.address,
                    delegate,
                },
            }),
        ]);
    }
    /**
     * Transfers funds from a pool to a loan and marks the start of the loan
     * terms.
     * @param delegate Pool delegate. Optional.
     * @param pool Pool that the loan is associated with
     * @param loan Loan to start.
     * @returns Transaction Envelope.
     */
    async fundLoan({ delegate = this.client.provider.wallet.publicKey, pool, loan, }) {
        const { address: treasuryLocker, instruction: createTreasuryIx } = await getOrCreateATA({
            provider: this.client.provider,
            mint: pool.data.baseMint,
            owner: pool.data.globals,
        });
        return this.client.provider.newTX([
            createTreasuryIx,
            this.client.program.instruction.loanFund({
                accounts: {
                    globals: pool.data.globals,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    delegate,
                    delegateClaimable: pool.data.delegateClaimable,
                    treasuryLocker,
                    pool: pool.address,
                    poolLocker: pool.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Takes a loan with an overdue repayment and closes it, returning any
     * possible remaining funds into the pool and deducting the remainder from the
     * pool value.
     * @param globals Globals account.
     * @param delegate Pool delegate. Optional.
     * @param pool Pool that the loan is associated with.
     * @param loan Loan account.
     * @returns Transaction Envelope.
     */
    async triggerDefault({ globals, delegate = this.client.provider.wallet.publicKey, pool, loan, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.loanDefault({
                accounts: {
                    globals: globals.address,
                    delegate,
                    pool: pool.address,
                    poolLocker: pool.data.locker,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Transfers funds from a pool to a loan and marks the start of the loan
     * terms.
     * @param delegate Pool delegate. Optional.
     * @param pool Pool that the loan is associated with
     * @param loan Loan to start.
     * @returns Transaction Envelope.
     */
    async fundOpenTermLoan({ delegate = this.client.provider.wallet.publicKey, pool, loan, }) {
        const { instruction: createTreasuryIx } = await getOrCreateATA({
            provider: this.client.provider,
            mint: pool.data.baseMint,
            owner: pool.data.globals,
        });
        return this.client.provider.newTX([
            createTreasuryIx,
            this.client.program.instruction.openTermLoanFund({
                accounts: {
                    delegate,
                    globals: pool.data.globals,
                    pool: pool.address,
                    poolLocker: pool.data.locker,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Takes an open term loan with an overdue repayment and closes it, returning any
     * possible remaining funds into the pool and deducting the remainder from the
     * pool value.
     * @param globals Globals account.
     * @param delegate Pool delegate. Optional.
     * @param pool Pool that the loan is associated with.
     * @param loan Loan account.
     * @returns Transaction Envelope.
     */
    async openTermLoanTriggerDefault({ globals, delegate = this.client.provider.wallet.publicKey, pool, loan, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.openTermLoanDefault({
                accounts: {
                    globals: globals.address,
                    delegate,
                    pool: pool.address,
                    poolLocker: pool.data.locker,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Delegate calls this to initiate a borrower repayment sequence.
     */
    async callOpenTermLoan({ delegate = this.client.provider.wallet.publicKey, pool, loan, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.openTermLoanCall({
                accounts: {
                    delegate,
                    globals: pool.data.globals,
                    pool: pool.address,
                    loan: loan.address,
                },
            }),
        ]);
    }
    async proposeRefinanceOpenTermLoan({ delegate = this.client.provider.wallet.publicKey, pool, loan, config, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.openTermLoanProposeRefinance(config, {
                accounts: {
                    delegate,
                    globals: pool.data.globals,
                    pool: pool.address,
                    loan: loan.address,
                },
            }),
        ]);
    }
    /**
     * Claims loan fees due to a pool delegate.
     * @param pool Pool to claim fees from.
     * @param delegate Pool delegate. Optional.
     * @param delegateLocker Token account to draw to. Optional, defaults to ATA.
     * @param claimAmount Amount to claim.
     * @returns Transaction Envelope.
     */
    async claimDelegateFees({ pool, delegate = this.client.provider.wallet.publicKey, delegateLocker, claimAmount, }) {
        const delegateLockerAccount = delegateLocker
            ? {
                address: delegateLocker,
                instruction: null,
            }
            : await getOrCreateATA({
                provider: this.client.provider,
                mint: pool.data.baseMint,
            });
        return this.client.provider.newTX([
            delegateLockerAccount.instruction,
            this.client.program.instruction.poolClaimDelegateFees(toBN(claimAmount), {
                accounts: {
                    pool: pool.address,
                    delegate,
                    delegateLocker: delegateLockerAccount.address,
                    delegateClaimable: pool.data.delegateClaimable,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Sets a pending delegate for pool delegate ownership transfer.
     * @param pool Pool to transfer delegate from.
     * @param delegate Current delegate. Optional.
     * @param nextDelegate New delegate.
     * @returns Transaction Envelope.
     */
    async setPendingDelegate({ pool, delegate = this.client.provider.wallet.publicKey, nextDelegate, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.poolSetPendingDelegate(nextDelegate, {
                accounts: {
                    pool: pool.address,
                    delegate,
                },
            }),
        ]);
    }
    /**
     * Accepts a pending delegate.
     * @param pool Pool that the delegate is being transferred from.
     * @param delegateNominee New delegate.
     * @returns Transaction Envelope.
     */
    async acceptDelegate({ pool, delegateNominee = pool.data.pendingDelegate, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.poolAcceptDelegate({
                accounts: {
                    pool: pool.address,
                    delegateNominee,
                    globals: pool.data.globals,
                },
            }),
        ]);
    }
}
/** Actions taken by a Lender. */
export class LenderActions {
    constructor(client) {
        this.client = client;
    }
    /**
     * Gets the public key of the provider.
     */
    get publicKey() {
        return this.client.provider.wallet.publicKey;
    }
    /**
     * Initializes a new lender account. This account acts as a place
     * to store all information associated with a given depositor such as
     * token balances, allowlisted status, and other miscellaneous information.
     * @param pool Pool to create a lender account for.
     * @param owner The owner of the account.
     * @param payer The payer of the transaction.
     * @returns Transaction Envelope.
     */
    async initializeLender({ pool, owner = this.client.provider.wallet.publicKey, payer = this.client.provider.wallet.publicKey, }) {
        const lenderShares = await getATAAddress({
            mint: pool.data.sharesMint,
            owner,
        });
        const [lender] = await this.client.findProgramAddress([
            "lender",
            pool.address,
            owner,
        ]);
        const [lenderLockedShares] = await this.client.findProgramAddress([
            "locked_shares",
            lender,
        ]);
        return {
            lender,
            lenderShares,
            lenderLockedShares,
            tx: this.client.provider.newTX([
                this.client.program.instruction.lenderInitialize({
                    accounts: {
                        payer,
                        owner,
                        pool: pool.address,
                        sharesMint: pool.data.sharesMint,
                        lender,
                        lenderShares,
                        lockedShares: lenderLockedShares,
                        systemProgram: SystemProgram.programId,
                        tokenProgram: spl.TOKEN_PROGRAM_ID,
                        associatedTokenProgram: spl.ASSOCIATED_TOKEN_PROGRAM_ID,
                        rent: web3.SYSVAR_RENT_PUBKEY,
                    },
                }),
            ]),
        };
    }
    /**
     * Deposits into a pool.
     * @param amount Amount of tokens to deposit.
     * @param pool Pool address.
     * @param lenderUser The user making the deposit.
     * @param lenderLocker Token account to deposit from, defaults to ATA.
     * @returns Transaction Envelope.
     */
    async deposit({ amount, pool, lenderUser = this.client.provider.wallet.publicKey, lenderLocker, }) {
        const lenderShares = await getATAAddress({
            mint: pool.data.sharesMint,
            owner: lenderUser,
        });
        const [lender] = await findLenderAddress(pool.address, lenderUser);
        const [lockedShares] = await this.client.findProgramAddress([
            "locked_shares",
            lender,
        ]);
        // Default to user's associated token account.
        const lenderLockerAccount = lenderLocker !== null && lenderLocker !== void 0 ? lenderLocker : (await getAndEnsureATA(this.client.provider, pool.data.baseMint, lenderUser));
        return this.client.provider.newTX([
            this.client.program.instruction.lenderDeposit(toBN(amount), {
                accounts: {
                    pool: pool.address,
                    globals: pool.data.globals,
                    poolLocker: pool.data.locker,
                    sharesMint: pool.data.sharesMint,
                    lender,
                    lenderUser,
                    lockedShares,
                    lenderShares,
                    lenderLocker: lenderLockerAccount,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                    rent: web3.SYSVAR_RENT_PUBKEY,
                },
            }),
        ]);
    }
    /**
     * Unlocks any locked tokens stored in a lender token account.
     * @param lender Lender account to unlock from.
     * @param lenderUser The owner of the lender account. Optional.
     * @param globals The globals account associated with the pool.
     * @returns Transaction Envelope.
     */
    async unlockShares({ lender, lenderUser = this.client.provider.wallet.publicKey, globals, }) {
        return this.client.provider.newTX([
            this.client.program.instruction.lenderUnlockDeposit({
                accounts: {
                    lenderUser,
                    pool: lender.data.pool,
                    globals: globals.address,
                    lender: lender.address,
                    lockedShares: lender.data.lockedShares,
                    lenderShares: lender.data.lenderShares,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Creates a new withdrawal request. A withdrawal request has
     * a cooldown time, after which there is a window that it can be
     * executed.
     * @param sharesAmount Amount of shares to withdraw.
     * @param pool Pool to withdraw from.
     * @param lender Lender account to withdraw from.
     * @param lenderUser Owner of lender account. Optional.
     * @returns Transaction Envelope.
     */
    async initializeWithdrawalRequest({ sharesAmount, pool, lender, lenderUser = this.client.provider.wallet.publicKey, }) {
        const nonce = Nonce.generate();
        const [withdrawalRequest] = await this.client.findProgramAddress([
            "withdrawal_request",
            lender.address,
            nonce,
        ]);
        const [withdrawalRequestLocker] = await this.client.findProgramAddress([
            "withdrawal_request_locker",
            withdrawalRequest,
        ]);
        return {
            withdrawalRequest,
            withdrawalRequestLocker,
            tx: this.client.provider.newTX([
                this.client.program.instruction.withdrawalRequestInitialize(nonce.asParam(), toBN(sharesAmount), {
                    accounts: {
                        pool: pool.address,
                        globals: pool.data.globals,
                        sharesMint: pool.data.sharesMint,
                        lender: lender.address,
                        lenderOwner: lenderUser,
                        lenderShareAccount: lender.data.lenderShares,
                        withdrawalRequest,
                        withdrawalRequestLocker,
                        systemProgram: SystemProgram.programId,
                        tokenProgram: spl.TOKEN_PROGRAM_ID,
                        rent: web3.SYSVAR_RENT_PUBKEY,
                    },
                }),
            ]),
        };
    }
    /**
     * Executes a withdrawal request. Can only be done after the cooldown
     * period has ended. This method will also close the withdrawal request account.
     * @param pool The pool that funds are being withdrawn from.
     * @param withdrawalRequest The withdrawal request being executed.
     * @param lenderLocker The account to withdraw funds to. Defaults to ATA.
     * @param lenderShareLocker Account to withdraw unused shares to after account close.
     * @returns Transaction Envelope.
     */
    async executeWithdrawalRequest({ shares, pool, withdrawalRequest, lenderUser = this.client.provider.wallet.publicKey, lenderLocker, lenderShareLocker, }) {
        shares = shares !== null && shares !== void 0 ? shares : withdrawalRequest.data.shares;
        // Default to user's ATA.
        const lenderTokenAccount = lenderLocker
            ? {
                address: lenderLocker,
                instruction: null,
            }
            : await getOrCreateATA({
                provider: this.client.provider,
                mint: pool.data.baseMint,
                owner: lenderUser,
            });
        // Default to user's ATA for pool shares.
        // The lender should already have a share account if they're attempting
        // to withdraw, so we don't attempt to auto-create one.
        lenderShareLocker =
            lenderShareLocker !== null && lenderShareLocker !== void 0 ? lenderShareLocker : (await getAndEnsureATA(this.client.provider, pool.data.sharesMint, lenderUser));
        return this.client.provider.newTX([
            lenderTokenAccount.instruction,
            this.client.program.instruction.withdrawalRequestExecute(toBN(shares), {
                accounts: {
                    pool: pool.address,
                    globals: pool.data.globals,
                    poolLocker: pool.data.locker,
                    sharesMint: pool.data.sharesMint,
                    lender: withdrawalRequest.data.lender,
                    lenderShareAccount: lenderShareLocker,
                    lenderOwner: lenderUser,
                    withdrawalRequest: withdrawalRequest.address,
                    withdrawalRequestLocker: withdrawalRequest.data.locker,
                    lenderLocker: lenderTokenAccount.address,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
            // Automatically close the withdrawal request after execution.
            this.client.program.instruction.withdrawalRequestClose({
                accounts: {
                    globals: pool.data.globals,
                    pool: pool.address,
                    lender: withdrawalRequest.data.lender,
                    lenderOwner: lenderUser,
                    lenderShareAccount: lenderShareLocker,
                    withdrawalRequest: withdrawalRequest.address,
                    withdrawalRequestLocker: withdrawalRequest.data.locker,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Closes a withdrawal request account.
     * @param pool The pool to withdraw from.
     * @param withdrawalRequest Account to close.
     * @param lenderUser Owner of withdrawal request.
     * @param lenderShareAccount Account to withdraw unused shares to after account close.
     * @returns Transaction Envelope.
     */
    async closeWithdrawalRequest({ pool, withdrawalRequest, lenderUser = this.client.provider.wallet.publicKey, lenderShareAccount, }) {
        // Default to user's ATA for pool shares.
        // The lender should already have a share account if they're attempting
        // to cancel a withdrawal, so we don't auto-create one.
        lenderShareAccount =
            lenderShareAccount !== null && lenderShareAccount !== void 0 ? lenderShareAccount : (await getAndEnsureATA(this.client.provider, pool.data.sharesMint, lenderUser));
        return this.client.provider.newTX([
            this.client.program.instruction.withdrawalRequestClose({
                accounts: {
                    globals: pool.data.globals,
                    pool: pool.address,
                    lender: withdrawalRequest.data.lender,
                    lenderOwner: lenderUser,
                    lenderShareAccount,
                    withdrawalRequest: withdrawalRequest.address,
                    withdrawalRequestLocker: withdrawalRequest.data.locker,
                    systemProgram: SystemProgram.programId,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
}
/** Actions taken by a Borrower. */
export class BorrowerActions {
    constructor(client) {
        this.client = client;
    }
    /**
     * Gets the public key of the provider.
     */
    get publicKey() {
        return this.client.provider.wallet.publicKey;
    }
    /**
     * Creates a new loan.
     * @param pool The pool to create a loan with.
     * @param borrower The borrower taking out the loan. Optional.
     * @param loanConfig The various loan configuration options.
     * @returns Transaction Envelope.
     */
    async initializeLoan({ pool, borrower = this.client.provider.wallet.publicKey, loanConfig, }) {
        const nonce = Nonce.generate();
        const [loan] = await this.client.findProgramAddress([
            "loan",
            borrower,
            nonce,
        ]);
        const [loanLocker] = await this.client.findProgramAddress([
            "loan_locker",
            loan,
        ]);
        return {
            loan,
            loanLocker,
            tx: this.client.provider.newTX([
                this.client.program.instruction.loanInitialize(nonce.asParam(), loanConfig, {
                    accounts: {
                        borrower,
                        loan,
                        loanLocker,
                        pool: pool.address,
                        baseMint: pool.data.baseMint,
                        globals: pool.data.globals,
                        systemProgram: SystemProgram.programId,
                        tokenProgram: spl.TOKEN_PROGRAM_ID,
                        rent: web3.SYSVAR_RENT_PUBKEY,
                    },
                }),
            ]),
        };
    }
    /**
     * After a loan has been funded, this method draws funds into
     * a borrower's token account.
     * @param borrower The loan borrower. Optional.
     * @param borrowerLocker The account to withdraw funds to.
     * @param globals The globals account associated with the loan pool.
     * @param loan The loan to drawdown funds from.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async drawdownFunds({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, globals, loan, pool, }) {
        const borrowerTokenAccount = borrowerLocker
            ? {
                address: borrowerLocker,
                instruction: null,
            }
            : await getOrCreateATA({
                provider: this.client.provider,
                mint: pool.data.baseMint,
                owner: borrower,
            });
        return this.client.provider.newTX([
            borrowerTokenAccount.instruction,
            this.client.program.instruction.loanDrawdown({
                accounts: {
                    globals: globals.address,
                    borrower,
                    borrowerLocker: borrowerTokenAccount.address,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Makes a payment to a loan.
     * @param borrower The borrower making the payment. Optional.
     * @param borrowerLocker The account to withdraw funds from.
     * @param globals The globals account associated with the loan pool.
     * @param loan The loan to make the payment to.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async makePayment({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, globals, loan, pool, }) {
        borrowerLocker =
            borrowerLocker !== null && borrowerLocker !== void 0 ? borrowerLocker : (await getAndEnsureATA(this.client.provider, pool.data.baseMint, borrower));
        const treasuryLocker = await getAndEnsureATA(this.client.provider, pool.data.baseMint, globals.address);
        return this.client.provider.newTX([
            this.client.program.instruction.loanMakePayment({
                accounts: {
                    globals: globals.address,
                    borrower,
                    loan: loan.address,
                    pool: pool.address,
                    borrowerLocker,
                    treasuryLocker,
                    delegateClaimable: pool.data.delegateClaimable,
                    poolLocker: pool.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Makes all loan repayments early, also known as 'calling a loan'.
     * NOTE: This method does not close the loan account.
     * @param borrower The borrower making the payment. Optional.
     * @param borrowerLocker The account to withdraw funds from.
     * @param globals The globals account associated with the loan pool.
     * @param loan The loan to make the payment to.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async earlyCloseLoan({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, globals, loan, pool, }) {
        borrowerLocker =
            borrowerLocker !== null && borrowerLocker !== void 0 ? borrowerLocker : (await getAndEnsureATA(this.client.provider, pool.data.baseMint, borrower));
        const treasuryLocker = await getAndEnsureATA(this.client.provider, pool.data.baseMint, globals.address);
        return this.client.provider.newTX([
            this.client.program.instruction.loanEarlyClose({
                accounts: {
                    globals: globals.address,
                    borrower,
                    loan: loan.address,
                    pool: pool.address,
                    borrowerLocker,
                    treasuryLocker,
                    delegateClaimable: pool.data.delegateClaimable,
                    poolLocker: pool.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Closes a loan account.
     * @param borrower The loan borrower.
     * @param borrowerLocker The account to withdraw any remaining funds to.
     * @param loan The loan to close.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async closeLoan({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, loan, pool, }) {
        const borrowerTokenAccount = borrowerLocker
            ? {
                address: borrowerLocker,
                instruction: null,
            }
            : await getOrCreateATA({
                provider: this.client.provider,
                mint: pool.data.baseMint,
                owner: borrower,
            });
        return this.client.provider.newTX([
            borrowerTokenAccount.instruction,
            this.client.program.instruction.loanClose({
                accounts: {
                    globals: pool.data.globals,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    borrower,
                    borrowerLocker: borrowerTokenAccount.address,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Closes an open term loan account.
     * @param borrower The loan borrower.
     * @param borrowerLocker The account to withdraw any remaining funds to.
     * @param loan The open term loan to close.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async closeOpenTermLoan({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, loan, pool, }) {
        const borrowerTokenAccount = borrowerLocker
            ? {
                address: borrowerLocker,
                instruction: null,
            }
            : await getOrCreateATA({
                provider: this.client.provider,
                mint: pool.data.baseMint,
                owner: borrower,
            });
        return this.client.provider.newTX([
            borrowerTokenAccount.instruction,
            this.client.program.instruction.openTermLoanClose({
                accounts: {
                    globals: pool.data.globals,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    borrower,
                    borrowerLocker: borrowerTokenAccount.address,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Creates a new loan.
     * @param pool The pool to create a loan with.
     * @param borrower The borrower taking out the loan. Optional.
     * @param loanConfig The various loan configuration options.
     * @returns Transaction Envelope.
     */
    async initializeOpenTermLoan({ pool, borrower = this.client.provider.wallet.publicKey, loanConfig, }) {
        const nonce = Nonce.generate();
        const [loan] = await this.client.findProgramAddress([
            "loan",
            borrower,
            nonce,
        ]);
        const [loanLocker] = await this.client.findProgramAddress([
            "loan_locker",
            loan,
        ]);
        return {
            loan,
            loanLocker,
            tx: this.client.provider.newTX([
                this.client.program.instruction.openTermLoanInitialize(nonce.asParam(), loanConfig, {
                    accounts: {
                        borrower,
                        loan,
                        loanLocker,
                        pool: pool.address,
                        baseMint: pool.data.baseMint,
                        globals: pool.data.globals,
                        systemProgram: SystemProgram.programId,
                        tokenProgram: spl.TOKEN_PROGRAM_ID,
                        rent: web3.SYSVAR_RENT_PUBKEY,
                    },
                }),
            ]),
        };
    }
    /**
     * After a loan has been funded, this method draws funds into
     * a borrower's token account.
     * @param borrower The loan borrower. Optional.
     * @param borrowerLocker The account to withdraw funds to.
     * @param globals The globals account associated with the loan pool.
     * @param loan The loan to drawdown funds from.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async openTermLoanDrawdown({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, globals, loan, pool, }) {
        const borrowerTokenAccount = borrowerLocker
            ? {
                address: borrowerLocker,
                instruction: null,
            }
            : await getOrCreateATA({
                provider: this.client.provider,
                mint: pool.data.baseMint,
                owner: borrower,
            });
        return this.client.provider.newTX([
            borrowerTokenAccount.instruction,
            this.client.program.instruction.openTermLoanDrawdown({
                accounts: {
                    globals: globals.address,
                    borrower,
                    borrowerLocker: borrowerTokenAccount.address,
                    loan: loan.address,
                    loanLocker: loan.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Makes a payment to a loan.
     * @param borrower The borrower making the payment. Optional.
     * @param borrowerLocker The account to withdraw funds from.
     * @param globals The globals account associated with the loan pool.
     * @param loan The loan to make the payment to.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async openTermLoanMakePayment({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, globals, loan, pool, }) {
        borrowerLocker =
            borrowerLocker !== null && borrowerLocker !== void 0 ? borrowerLocker : (await getAndEnsureATA(this.client.provider, pool.data.baseMint, borrower));
        const treasuryLocker = await getAndEnsureATA(this.client.provider, pool.data.baseMint, globals.address);
        return this.client.provider.newTX([
            this.client.program.instruction.openTermLoanMakePayment({
                accounts: {
                    globals: globals.address,
                    borrower,
                    loan: loan.address,
                    pool: pool.address,
                    borrowerLocker,
                    treasuryLocker,
                    delegateClaimable: pool.data.delegateClaimable,
                    poolLocker: pool.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    async openTermLoanAcceptRefinance({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, globals, loan, pool, }) {
        borrowerLocker =
            borrowerLocker !== null && borrowerLocker !== void 0 ? borrowerLocker : (await getAndEnsureATA(this.client.provider, pool.data.baseMint, borrower));
        const treasuryLocker = await getAndEnsureATA(this.client.provider, pool.data.baseMint, globals.address);
        return this.client.provider.newTX([
            this.client.program.instruction.openTermLoanAcceptRefinance({
                accounts: {
                    globals: globals.address,
                    borrower,
                    loan: loan.address,
                    pool: pool.address,
                    borrowerLocker,
                    treasuryLocker,
                    delegateClaimable: pool.data.delegateClaimable,
                    poolLocker: pool.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
    /**
     * Makes all loan repayments early, also known as 'calling a loan'.
     * NOTE: This method does not close the loan account.
     * @param borrower The borrower making the payment. Optional.
     * @param borrowerLocker The account to withdraw funds from.
     * @param globals The globals account associated with the loan pool.
     * @param loan The loan to make the payment to.
     * @param pool The pool the loan is associated with.
     * @returns Transaction Envelope.
     */
    async openTermLoanRepay({ borrower = this.client.provider.wallet.publicKey, borrowerLocker, globals, loan, pool, }) {
        borrowerLocker =
            borrowerLocker !== null && borrowerLocker !== void 0 ? borrowerLocker : (await getAndEnsureATA(this.client.provider, pool.data.baseMint, borrower));
        const treasuryLocker = await getAndEnsureATA(this.client.provider, pool.data.baseMint, globals.address);
        return this.client.provider.newTX([
            this.client.program.instruction.openTermLoanRepay({
                accounts: {
                    globals: globals.address,
                    borrower,
                    loan: loan.address,
                    pool: pool.address,
                    borrowerLocker,
                    treasuryLocker,
                    delegateClaimable: pool.data.delegateClaimable,
                    poolLocker: pool.data.locker,
                    tokenProgram: spl.TOKEN_PROGRAM_ID,
                },
            }),
        ]);
    }
}
//# sourceMappingURL=syrup.js.map